import { KeyboardArrowDown } from "@mui/icons-material";
import { ButtonBase, Divider, Menu, MenuItem, Typography } from "@mui/material";
import ConversationsStore from "components/Conversations/Stores/ConversationsStore";
import { observer } from "mobx-react";
import React, { useState } from "react";
import { useStore } from "utils/useStore";
import { durationDisplay } from "components/SoundClipEditor/Controls/Player";
import OutlineDefinitionsDialog from "./OutlineDefinitionsDialog";

const formatHeadingForTestId = (heading: string) =>
    heading.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const SegmentJumpToDropdown: React.FC = observer(() => {
    const store = useStore(ConversationsStore);
    const transcriptionStore = store.transcriptionStore;
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [isOutlineDefinitionsOpen, setIsOutlineDefinitionsOpen] =
        useState(false);
    const handleCloseMenu = () => setAnchorEl(null);

    const segments = store.conversationSegments;

    if (segments.length === 0) return null;

    const visibleSections = (
        transcriptionStore?.segmentedBlurbSections ?? []
    ).filter((section) => section.showInJumpTo);

    if (visibleSections.length === 0) return null;

    return (
        <>
            <ButtonBase
                onClick={(e) => setAnchorEl(e.currentTarget)}
                id="convo-details-outline-dropdown"
                data-testid="convo-details-outline-dropdown-button"
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    border: "1px solid",
                    borderColor: "lightgray.main",
                    borderRadius: "6px",
                    p: 1,
                    "&:hover": {
                        backgroundColor: "action.hover",
                    },
                    width: "240px",
                }}
            >
                <Typography
                    sx={{
                        fontSize: 14,
                        color: "text.secondary",
                    }}
                >
                    Jump to
                </Typography>
                <KeyboardArrowDown
                    sx={{ color: "text.secondary", ml: "auto" }}
                />
            </ButtonBase>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleCloseMenu}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                slotProps={{
                    paper: {
                        sx: {
                            mt: 0.5,
                            minWidth: 240,
                            maxHeight: 400,
                            borderRadius: 1,
                        },
                    },
                }}
            >
                {visibleSections.map((section) => (
                    <MenuItem
                        key={section.segment.id}
                        data-pendo-id={`convo-details-outline-${section.segment.heading}`}
                        data-testid={`convo-details-outline-item-${formatHeadingForTestId(section.segment.heading)}`}
                        onClick={() => {
                            store.soundClipEditorStore.setPlayHeadTime(
                                section.startTimestamp,
                                true,
                            );

                            if (transcriptionStore) {
                                const sectionHeaderIndex =
                                    transcriptionStore.transcriptionDisplayRows.findIndex(
                                        (row) =>
                                            row.type === "segmentHeader" &&
                                            row.section.segment.id ===
                                                section.segment.id,
                                    );
                                if (sectionHeaderIndex !== -1) {
                                    transcriptionStore.setScrollIndex(
                                        sectionHeaderIndex,
                                    );
                                }
                            }
                            handleCloseMenu();
                        }}
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 1,
                            py: 1,
                            px: 2,
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: 14,
                                fontWeight: 500,
                            }}
                        >
                            {section.segment.heading}
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: 14,
                                color: "text.secondary",
                            }}
                        >
                            {durationDisplay(section.startTimestamp)}
                        </Typography>
                    </MenuItem>
                ))}
                <Divider />
                <MenuItem
                    id="convo-details-view-outline-definitions"
                    onClick={() => {
                        handleCloseMenu();
                        setIsOutlineDefinitionsOpen(true);
                    }}
                >
                    <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                        View Outline Definitions
                    </Typography>
                </MenuItem>
            </Menu>
            <OutlineDefinitionsDialog
                isOpen={isOutlineDefinitionsOpen}
                onClose={() => setIsOutlineDefinitionsOpen(false)}
            />
        </>
    );
});

export default SegmentJumpToDropdown;
